import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { validateData, clearError } from "../../validators/YupValidations";
import { verifyOtpAndResetSchema } from "../../validators/validatorSchema";

export default function VerifyOtpAndResetPassword({
  isOpen,
  onClose,
  onVerifyReset,
  onResend,
  otp_id,
}) {
  const [form, setForm] = useState({
    otp: "",
    password: "",
    confirmPwd: "",
  });

  const [errors, setErrors] = useState({});
  const [timer, setTimer] = useState(30);
  const [resendCount, setResendCount] = useState(0);

  const timerRef = useRef(null);
  const inputRef = useRef(null);

  // Keep OTP Timer Logic (unchanged)
  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTimer(30);

    timerRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  // Reset when modal opens
  useEffect(() => {
    if (!isOpen) return;

    setForm({ otp: "", password: "", confirmPwd: "" });
    setErrors({});
    setResendCount(0);
    startTimer();

    setTimeout(() => inputRef.current?.focus(), 120);

    return () => timerRef.current && clearInterval(timerRef.current);
  }, [isOpen]);

  // -----------------------------
  // HANDLE FIELD CHANGE
  // -----------------------------
  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });

    // clear error instantly
    if (errors[field]) clearError(setErrors, field);
  };

  const formattedTimer = () => {
    const s = timer % 60;
    const m = Math.floor(timer / 60);
    return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s}s`;
  };

  // -----------------------------
  // SUBMIT WITH YUP VALIDATION
  // -----------------------------
  const handleSubmit = async () => {
    const isValid = await validateData(verifyOtpAndResetSchema, form, setErrors);
    if (!isValid) return;

    onVerifyReset(form.otp, form.password, otp_id);
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>Reset Password</Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 pb-4">
        <p className="text-center">Enter the OTP and your new password.</p>

        {/* OTP FIELD */}
        <label className="small fw-semibold mb-1">OTP</label>
        <Form.Control
          ref={inputRef}
          type="text"
          inputMode="numeric"
          maxLength={6}
          className={`p-3 text-center fs-4 ${errors.otp ? "is-invalid" : ""}`}
          placeholder="Enter OTP"
          value={form.otp}
          onChange={(e) => handleChange("otp", e.target.value.replace(/\D/g, ""))}
        />
        {errors.otp && <div className="invalid-feedback d-block">{errors.otp.message}</div>}

        {/* RESEND SECTION */}
        <div className="text-center mt-2">
          {resendCount >= 5 ? (
            <p className="text-danger fw-bold">Maximum resend attempts reached.</p>
          ) : timer > 0 ? (
            <p>
              Resend OTP in <strong>{formattedTimer()}</strong>
            </p>
          ) : (
            <Button
              variant="link"
              className="forgot-link mx-auto"
              onClick={() => {
                onResend && onResend(otp_id);
                setResendCount((c) => c + 1);
                startTimer();
              }}
            >
              Resend OTP
            </Button>
          )}
        </div>

        {/* PASSWORD */}
        <label className="small fw-semibold mb-1 mt-3">New Password</label>
        <Form.Control
          type="password"
          className={`p-3 ${errors.password ? "is-invalid" : ""}`}
          placeholder="Enter new password"
          value={form.password}
          onChange={(e) => handleChange("password", e.target.value)}
        />
        {errors.password && (
          <div className="invalid-feedback d-block">{errors.password.message}</div>
        )}

        {/* CONFIRM PASSWORD */}
        <label className="small fw-semibold mb-1 mt-3">Confirm New Password</label>
        <Form.Control
          type="password"
          className={`p-3 ${errors.confirmPwd ? "is-invalid" : ""}`}
          placeholder="Confirm new password"
          value={form.confirmPwd}
          onChange={(e) => handleChange("confirmPwd", e.target.value)}
        />
        {errors.confirmPwd && (
          <div className="invalid-feedback d-block">{errors.confirmPwd.message}</div>
        )}

        {/* FOOTER BUTTONS */}
        <Modal.Footer className="border-0 p-0 mt-3 d-flex flex-column">
          <Button className="btn-orange w-100 mb-3" onClick={handleSubmit}>
            Reset Password
          </Button>

          <Button className="btn-light w-100" onClick={onClose}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal.Body>
    </Modal>
  );
}
